import axiosIns from '@axiosIns'
import axios from 'axios'
import Vue from 'vue'
import getMessageIdentity from './messageIdentity'

const DEFAULT_CURRENT_PAGE = 1
const DEFAULT_PAGE_SIZE = 50
let cancelTokenSourceDetails = null
let cancelTokenSourceList = null

const createInitialState = () => ({
  activeYoungId: null,
  activeYoungDetails: null,
  messages: [],
  youngs: [],
  blockedYoungs: [],
  LastContacts: [],
  youngsComment: [],
  blockedBy: {},
  excelDatas: null,
  zipDatas: null,
  // Unread message counts keyed by youngId. Kept separate from the `youngs` array on purpose:
  // the array is entirely replaced whenever the list is (re)fetched (e.g. every time the
  // Youngs page is mounted), which would otherwise silently wipe out any unread badges
  // accumulated while the user was on a different page. This map is the durable source of
  // truth for unread counts and is merged back onto youngs whenever the list is (re)populated.
  unreadMessageCounts: {},
  pagination: {
    currentPage: DEFAULT_CURRENT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    hasNext: false,
    hasPrevious: false,
  },
  searchParams: {
    searchInput: '',
    filterByMissedCalls: false,
    filterByPendingConversation: false,
  },
  isInitialLoad: true,
})

const cancelPendingDetailRequests = () => {
  if (cancelTokenSourceDetails) {
    cancelTokenSourceDetails.cancel()
  }
  cancelTokenSourceDetails = axios.CancelToken.source()
}

const cancelPendingListRequests = () => {
  if (cancelTokenSourceList) {
    cancelTokenSourceList.cancel()
  }
  cancelTokenSourceList = axios.CancelToken.source()
}

export default {
  namespaced: true,
  state: createInitialState(),
  getters: {
    getLastContact: state => id => state.LastContacts.find(lastContact => lastContact.id === id),
    activeYoung: (state, getters) => {
      if (state.activeYoungDetails) {
        return state.activeYoungDetails
      }
      return state.activeYoungId
        ? getters.youngs.find(y => y.id === state.activeYoungId) || {}
        : {}
    },
    messages: (state, getters) => state.messages
        .map(message => ({
          ...message,
          user: message.userId ? getters.getLastContact(message.userId) : null,
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date)),
    youngsComment: state => state.youngsComment,
    youngs: (state, getters) => {
      const processedYoungs = state.youngs.map(young => ({
        ...young,
        lastMessageUser: young.lastMessageUserId
          ? getters.getLastContact(young.lastMessageUserId)
          : null,
      }))
      // Le tri est maintenant géré par l'API lorsque filterByMissedCalls est false.
      // Si filterByMissedCalls est true, l'API fournit déjà le bon ordre.
      return processedYoungs
    },
    blockedYoungs: (state, getters) => {
      const processedYoungs = state.blockedYoungs.map(young => ({
        ...young,
        lastMessageUser: young.lastMessageUserId
          ? getters.getLastContact(young.lastMessageUserId)
          : null,
      }))
      // Le tri est maintenant géré par l'API lorsque filterByMissedCalls est false.
      // Si filterByMissedCalls est true, l'API fournit déjà le bon ordre.
      return processedYoungs
    },
    getBlockedBy: state => state.blockedBy,
    excelDatas: state => state.excelDatas,
    zipDatas: state => state.zipDatas,
    pagination: state => state.pagination,
    searchParams: state => state.searchParams,
  },
  mutations: {
    addOrUpdateMessage(state, { message, hasMessagesPermission }) {
      if (state.activeYoungId === message.youngId) {
        const messageIdentity = getMessageIdentity(message)
        state.messages = state.messages
          .filter(existingMessage => getMessageIdentity(existingMessage) !== messageIdentity)
          .concat([message])
      }

      // SMS (type=1) or WhatsApp (type=2) from a Young -> pending; from a User → not pending; calls leave it unchanged
      const isSmsOrWhatsApp = !message.isInitialMessage && (message.type === 1 || message.type === 2)
      const isInboundFromYoung = message.senderType === 1

      // Track the unread count independently of whether the young is currently loaded in
      // `state.youngs` (e.g. the user is on another page and the list hasn't been fetched
      // yet this session), so no inbound message is ever missed. Only for users who actually
      // hold the "Messages" permission — others must never accumulate or see unread badges.
      if (hasMessagesPermission && isSmsOrWhatsApp && isInboundFromYoung && state.activeYoungId !== message.youngId) {
        Vue.set(state.unreadMessageCounts, message.youngId, (state.unreadMessageCounts[message.youngId] || 0) + 1)
      }

      const youngIndex = state.youngs.findIndex(y => y.id === message.youngId)
      if (youngIndex >= 0) {
        const young = state.youngs[youngIndex]
        const hasPendingMessage = isSmsOrWhatsApp
          ? isInboundFromYoung && !young.whoTakesCareUserId
          : young.hasPendingMessage
        const messageDate = new Date(message.date).getTime()
        const previousActivityDate = new Date(young.lastActivityDate).getTime()
        const isLatestInteraction = !Number.isNaN(messageDate)
          && (Number.isNaN(previousActivityDate) || messageDate >= previousActivityDate)
        Vue.set(state.youngs, youngIndex, {
          ...young,
          lastMessageDate: isLatestInteraction ? message.date : young.lastMessageDate,
          lastActivityDate: isLatestInteraction ? message.date : young.lastActivityDate,
          lastMessageUserId: isLatestInteraction && message.userId
            ? message.userId
            : young.lastMessageUserId,
          hasPendingMessage: isLatestInteraction ? hasPendingMessage : young.hasPendingMessage,
          hasSmsConversation: young.hasSmsConversation || isSmsOrWhatsApp,
          unreadMessages: hasMessagesPermission ? (state.unreadMessageCounts[message.youngId] || 0) : 0,
        })
        // Move young to top when the latest interaction changes
        // Only in default sort mode (not when using special filters)
        const isInDefaultMode = !state.searchParams.filterByMissedCalls
          && !state.searchParams.filterByPendingConversation
        if (isInDefaultMode && isLatestInteraction) {
          const updatedYoung = state.youngs[youngIndex]
          state.youngs.splice(youngIndex, 1)
          state.youngs.unshift(updatedYoung)
        }
      }
    },
    addOrUpdateYoung(state, young) {
      const pendingUnreadCount = state.unreadMessageCounts[young.id] || 0
      const index = state.youngs.findIndex(y => y.id === young.id)
      if (index >= 0) {
        // Garde la position du jeune dans la liste
        Vue.set(state.youngs, index, { ...state.youngs[index], ...young, unreadMessages: pendingUnreadCount })
      } else {
        state.youngs.unshift({ ...young, unreadMessages: pendingUnreadCount })
      }

      if (state.activeYoungId === young.id && state.activeYoungDetails) {
        state.activeYoungDetails = { ...state.activeYoungDetails, ...young }
      }
    },
    deleteMessage(state, id) {
      state.messages = state.messages.filter(m => m.id !== id)
    },
    deleteYoung(state, id) {
      if (state.activeYoungId === id) {
        state.activeYoungId = null
      }
      state.youngs = state.youngs.filter(y => y.id !== id)
      state.messages = state.messages.filter(m => m.youngId !== id)
      Vue.delete(state.unreadMessageCounts, id)
    },
    // Clears the cached youngs/messages/active conversation from a previous session so a
    // subsequent login for a different account (with different permissions/assignments)
    // doesn't keep showing stale conversations or permission-driven message actions.
    resetState(state) {
      Object.assign(state, createInitialState())
    },
    setActiveYoungId(state, id) {
      state.activeYoungId = id
      state.messages = state.messages.filter(m => m.youngId === id)
      // Clear unread counter when the conversation is opened
      Vue.set(state.unreadMessageCounts, id, 0)
      const young = state.youngs.find(y => y.id === id)
      if (young) {
        Vue.set(young, 'unreadMessages', 0)
      }
    },
    updateActiveYoungDetails(state, youngDetails) {
      state.activeYoungDetails = youngDetails
    },
    replaceMessages(state, messages) {
      // Fully replace messages for the active young used after permission changes (e.g. take charge)
      // Keeps cached messages for other youngs intact
      state.messages = [
        ...state.messages.filter(m => m.youngId !== state.activeYoungId),
        ...messages,
      ]
    },
    updateMessages(state, messages) {
      const allMessages = state.messages
        .filter(m => m.youngId === state.activeYoungId)
        .concat(messages)
      const distinctMessages = []
      const identityMap = new Map()
      allMessages.forEach(message => {
        const messageIdentity = getMessageIdentity(message)
        if (!identityMap.has(messageIdentity)) {
          identityMap.set(messageIdentity, true)
          distinctMessages.push(message)
        }
      })
      state.messages = distinctMessages
    },
    updateYoungs(state, youngs) {
      // Re-apply any unread counts accumulated while this list wasn't loaded (e.g. the user was
      // on another page), since the API response has no concept of per-viewer unread messages.
      state.youngs = youngs.map(young => ({
        ...young,
        unreadMessages: state.unreadMessageCounts[young.id] || 0,
      }))
    },
    appendYoungs(state, youngs) {
      // Append new youngs to the existing array, avoiding duplicates
      const youngIds = new Set(state.youngs.map(y => y.id))
      const newYoungs = youngs
        .filter(y => !youngIds.has(y.id))
        .map(young => ({
          ...young,
          unreadMessages: state.unreadMessageCounts[young.id] || 0,
        }))
      state.youngs = [...state.youngs, ...newYoungs]
    },
    updatePagination(state, { responseDataLength, pageNumber, pageSize }) {
      state.pagination.currentPage = pageNumber
      state.pagination.pageSize = pageSize
      state.pagination.hasNext = responseDataLength === pageSize && responseDataLength > 0
      state.pagination.hasPrevious = pageNumber > 1
    },
    updateSearchParams(state, params) {
      state.searchParams = {
        searchInput:
          params.searchInput !== undefined
            ? params.searchInput
            : state.searchParams.searchInput,
        filterByMissedCalls:
          params.filterByMissedCalls !== undefined
            ? params.filterByMissedCalls
            : state.searchParams.filterByMissedCalls,
        filterByPendingConversation:
          params.filterByPendingConversation !== undefined
            ? params.filterByPendingConversation
            : state.searchParams.filterByPendingConversation,
      }
    },
    changeStatusToPending(state, id) {
      const foundIndex = state.youngs.findIndex(x => x.id === id)
      if (foundIndex >= 0) {
        Vue.set(state.youngs, foundIndex, { ...state.youngs[foundIndex], conversationStatus: 1, whoTakesCareUserId: null })
      }
      if (state.activeYoungDetails && state.activeYoungDetails.id === id) {
        state.activeYoungDetails = { ...state.activeYoungDetails, conversationStatus: 1, whoTakesCareUserId: null }
      }
    },
    updateConversationStatus(state, { youngId, conversationStatus, userId }) {
      const foundIndex = state.youngs.findIndex(x => x.id === youngId)
      if (foundIndex >= 0) {
        // Clear hasPendingMessage when a handler takes charge (userId set)
        // OR when the conversation reaches a terminal state (Closed=3, GdprAutoArchived=4, GdprArchived=5)
        const isTerminalStatus = conversationStatus === 3 || conversationStatus === 4 || conversationStatus === 5
        Vue.set(state.youngs, foundIndex, {
          ...state.youngs[foundIndex],
          conversationStatus,
          whoTakesCareUserId: userId,
          hasPendingMessage: (userId || isTerminalStatus) ? false : state.youngs[foundIndex].hasPendingMessage,
        })
      }
      if (state.activeYoungDetails && state.activeYoungDetails.id === youngId) {
        state.activeYoungDetails = {
          ...state.activeYoungDetails,
          conversationStatus,
          whoTakesCareUserId: userId,
        }
      }
    },
    fetchedYoungComments(state, comments) {
      state.youngsComment = comments
    },
    fetchedBlockedYoungs(state, blockedYoungs) {
      state.blockedYoungs = blockedYoungs
    },
    updatedComment() {},
    updateYoung(state, updatedYoung) {
      state.youngs = state.youngs.map(young => (young.id === updatedYoung.id ? { ...young, ...updatedYoung } : young))

      if (state.activeYoungId === updatedYoung.id && state.activeYoungDetails) {
        state.activeYoungDetails = { ...state.activeYoungDetails, ...updatedYoung }
      }
    },
    addedComment(state, comment) {
      state.youngsComment.push(comment)
    },
    fetchedBlockedBy(state, blockedBy) {
      state.blockedBy = blockedBy
    },
    updateExcelDatas(state, excelDatas) {
      state.excelDatas = excelDatas
    },
    updateZipDatas(state, zipDatas) {
      state.zipDatas = zipDatas
    },
    fetchLastContacts(state, LastContacts) {
      state.LastContacts = LastContacts
    },
    clearYoungs(state) {
      state.youngs = []
      state.pagination.currentPage = DEFAULT_CURRENT_PAGE
      state.pagination.hasNext = false
    },
    setInitialLoadStatus(state, status) {
      state.isInitialLoad = status
    },
  },
  actions: {
    getYoungIdsOrderedByLastMissedCall() {
      return new Promise((resolve, reject) => {
        axiosIns
          .get('Young/GetYoungIdsOrderedByLastMissedCall')
          .then(response => {
            resolve(response.data)
          })
          .catch(error => reject(error))
      })
    },
    addOrUpdateMessage({ commit, rootGetters }, message) {
      // "Messages" permission business rule (mirrors `hasSmsPermission` in Youngs.vue): only
      // Coordinateurs/Écoutants with `canSendMessage` enabled should ever see unread message
      // badges/notifications. Computed here — next to the mutation that owns the badge state —
      // so it always reflects the CURRENTLY authenticated user, even right after an account
      // switch (no stale badges leaking from a previous, differently-permissioned user).
      const currentUser = rootGetters['users/currentUser']
      const roles = currentUser?.roles || []
      const hasEligibleRole = roles.includes('LeRefuge.Coordinateur') || roles.includes('LeRefuge.Ecoutant')
      const hasMessagesPermission = hasEligibleRole && currentUser?.profile?.canSendMessage === true

      commit('addOrUpdateMessage', { message, hasMessagesPermission })
    },
    addOrUpdateYoung({ commit }, young) {
      commit('addOrUpdateYoung', young)
    },
    deleteYoung({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .delete(`Young/${youngId}`)
          .then(response => {
            commit('deleteYoung', youngId)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    setActiveYoungId({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        cancelPendingDetailRequests()
        commit('setActiveYoungId', youngId)

        Promise.all([
          axiosIns.get(`Young/${youngId}`, {
            cancelToken: cancelTokenSourceDetails.token,
          }),
          axiosIns.get(`Young/${youngId}/Messages`, {
            cancelToken: cancelTokenSourceDetails.token,
          }),
        ])
          .then(([youngDetailsResponse, messagesResponse]) => {
            commit('updateActiveYoungDetails', youngDetailsResponse.data)
            commit('replaceMessages', messagesResponse.data)
            resolve({ young: youngDetailsResponse.data, messages: messagesResponse.data })
          })
          .catch(error => reject(error))
      })
    },
    fetchYoungs({ commit, state }, params = {}) {
      return new Promise((resolve, reject) => {
        cancelPendingListRequests()

        const pageNumber = params.page || 1
        const pageSize = params.pageSize || state.pagination.pageSize

        let apiSearchInput = ''
        let apiFilterByMissedCalls = false
        let apiFilterByPendingConversation = false

        if (pageNumber === 1) {
          apiSearchInput = params.searchQuery !== undefined ? params.searchQuery : ''
          apiFilterByMissedCalls = params.isFilteringByAbsencesCalls !== undefined
              ? params.isFilteringByAbsencesCalls
              : false
          apiFilterByPendingConversation = params.isFilteringByPendingConversation !== undefined
              ? params.isFilteringByPendingConversation
              : false

          commit('updateSearchParams', {
            searchInput: apiSearchInput,
            filterByMissedCalls: apiFilterByMissedCalls,
            filterByPendingConversation: apiFilterByPendingConversation,
          })
          commit('clearYoungs')
        } else {
          apiSearchInput = state.searchParams.searchInput
          apiFilterByMissedCalls = state.searchParams.filterByMissedCalls
          apiFilterByPendingConversation = state.searchParams.filterByPendingConversation
        }

        const queryParams = new URLSearchParams({
          pageNumber,
          pageSize,
          searchInput: apiSearchInput,
          filterByMissedCalls: apiFilterByMissedCalls,
          filterByPendingConversation: apiFilterByPendingConversation,
          detailed: false,
        })

        axiosIns
          .get(`Young?${queryParams.toString()}`, {
            cancelToken: cancelTokenSourceList.token,
          })
          .then(response => {
            commit('updatePagination', {
              responseDataLength: response.data.length,
              pageNumber,
              pageSize,
            })

            if (pageNumber === 1) {
              commit('updateYoungs', response.data)
            } else {
              commit('appendYoungs', response.data)
            }
            resolve(response)
          })
          .catch(error => {
            if (!axios.isCancel(error)) {
              reject(error)
            } else {
              resolve({ data: [] })
            }
          })
      })
    },
    fetchBlockedYoungs({ commit }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get('Young/blocked')
          .then(response => {
            commit('fetchedBlockedYoungs', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    changeConversationStatus({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .put(`Young/${youngId}/PendingConversation`)
          .then(response => {
            commit('changeStatusToPending', youngId)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchYoungComments({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`Young/${youngId}/Comments`)
          .then(response => {
            commit('fetchedYoungComments', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    updateComment({ commit }, data) {
      const body = {
        comment: data.comment,
        commentId: data.commentId,
      }
      return new Promise((resolve, reject) => {
        axiosIns
          .put(`Young/${data.youngId}/UpdateComment`, body)
          .then(response => {
            commit('updatedComment', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    addComment({ commit }, data) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`Young/${data.youngId}/AddComment`, data.comment, {
            headers: { 'Content-Type': 'application/json' },
          })
          .then(response => {
            commit('addedComment', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    updateYoungDetails({ commit }, data) {
      return new Promise((resolve, reject) => {
        axiosIns
          .put('Young', data)
          .then(async response => {
            const youngResponse = await axiosIns.get(`Young/${data.id}`)
            commit('updateYoung', youngResponse.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchBlockedBy({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`Young/${youngId}/BlockedBy`)
          .then(response => {
            commit('fetchedBlockedBy', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchExcelDatas({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`Young/${youngId}/ExportExcel`, {}, { responseType: 'blob' })
          .then(response => {
            commit('updateExcelDatas', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchZipDatas({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`Young/${youngId}/ExportZip`, {}, { responseType: 'blob' })
          .then(response => {
            commit('updateZipDatas', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchLastContacts({ commit }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get('Young/LastContacts')
          .then(response => {
            commit('fetchLastContacts', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    sendAccommodationRequest(context, { youngId, accommodationRequestComment }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`MailSender/${youngId}`, accommodationRequestComment ? { accommodationRequestComment } : {})
          .then(response => {
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    async sendMessage({ commit, state }, { youngId, body }) {
      const response = await axiosIns.post('Message/Outgoing', { youngId, body })
      const messagesResponse = await axiosIns.get(`Young/${youngId}/Messages`)
      if (state.activeYoungId === youngId) {
        commit('replaceMessages', messagesResponse.data)
      }
      return response
    },
    openConversation({ commit, dispatch, rootGetters }, youngId) {
      return axiosIns.post(`User/OpenConversation/${youngId}`).then(response => {
        const currentUser = rootGetters['users/currentUser']
        commit('updateConversationStatus', {
          youngId,
          conversationStatus: 2,
          userId: currentUser?.id || null,
        })
        // Reload messages now that we're the handler (backend now allows SMS/WhatsApp access)
        return dispatch('setActiveYoungId', youngId).then(() => response)
      })
    },
    closeConversation({ commit }, youngId) {
      return axiosIns.put(`User/CloseConversation/${youngId}`).then(response => {
        commit('updateConversationStatus', {
          youngId,
          conversationStatus: 3,
          userId: null,
        })
        return response
      })
    },
    gdprArchiveYoung({ commit }, youngId) {
      return axiosIns.put(`User/GdprArchiveYoung/${youngId}`).then(response => {
        commit('updateConversationStatus', {
          youngId,
          conversationStatus: 5,
          userId: null,
        })
        commit('updateYoung', { id: youngId, archivedForFiveYears: true })
        return response
      })
    },
  },
}
